import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiFragment } from '../../../entities/api-fragment.entity';
import { RecordType } from '../../../entities/metadata.entity';
import { rolesOf } from '../../../helpers/format-badges.helper';
import { toRecordFormat } from '../../../helpers/record-format.helper';
import { BaseColumnComponent } from '../../../services/base-column-component';

/**
 * The `Bestandsformaat` column on the detail surfaces, printed as the same tag the tree's `Formaten` column
 * prints — one record, so one tag, where {@link StukFormatsCellComponent} draws a stuk's whole set.
 *
 * The two columns stay different *questions*: this one is the record's own format, and answers from the record
 * in hand at no request cost. Pointing the batched lookup at these rows instead would leave the dossier tab's
 * stukken on a permanent skeleton — nothing primes {@link StukFormatsService} outside the tree.
 */
@Component({
    standalone: false,
    selector: 'digihaven-format-badge-cell',
    templateUrl: './format-badge-cell.component.html',
    styleUrls: ['./format-badge-cell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormatBadgeCellComponent extends BaseColumnComponent<ApiFragment> {
    badge$: Observable<{ label: string; previewRecordId: string | null; roles: string[] } | null>;

    constructor(translate: TranslateService, cd: ChangeDetectorRef) {
        super(cd);

        this.badge$ = this.data$.pipe(
            map(record => {
                const label: string | null = toRecordFormat(record, translate.instant('hierarchy.formats.analog'));

                return label
                    ? {
                          label,
                          previewRecordId: FormatBadgeCellComponent.previewRecordId(record),
                          roles: rolesOf(record, translate)
                      }
                    : null;
            })
        );
    }

    private static previewRecordId(record: ApiFragment): string | null {
        const isAccessRepresentation: boolean =
            record.getRecordType() === RecordType.Representation && record.metadata.Administrative?.IsAccess === true;

        return isAccessRepresentation ? record.getId() : null;
    }
}
